/**
 * useAiIndexWorker — AI 后台文件索引钩子
 *
 * Architecture 3.0 Phase 4: 从 AppLayout.tsx 抽离
 * 封装 AI 标签索引的后台扫描/批量生成/云端同步逻辑
 */

import { useState, useEffect, useCallback } from 'react';
import {
  AiIndex,
  readAiIndex,
  writeAiIndex,
  scanDirectory,
  buildFileTreeListing,
  getFilesToIndex,
  getFileLastModifiedMap,
  generateTagsBatch,
} from './useFileSystem';
import { ResourceApiService } from '../services/api';

// ─── Hook ──────────────────────────────────

export function useAiIndexWorker(localDirHandle: any) {
  const [aiIndex, setAiIndex] = useState<AiIndex | null>(null);
  const [isIndexing, setIsIndexing] = useState(false);
  const [indexingProgress, setIndexingProgress] = useState<{ current: number; total: number } | null>(null);
  const [localFileTreeListing, setLocalFileTreeListing] = useState<string>('');

  // ─── 后台静默索引 ─────────────────────────
  useEffect(() => {
    if (!localDirHandle) return;

    let cancelled = false;

    const runBackgroundIndexing = async () => {
      try {
        // 1. 读取现有索引
        const existingIndex = await readAiIndex(localDirHandle);
        setAiIndex(existingIndex);

        // 2. 扫描文件树（含 lastModified）
        const fileTree = await scanDirectory(localDirHandle);

        // 3. 找出需要索引的文件（新文件 + 修改过的文件）
        const toIndex = getFilesToIndex(fileTree, existingIndex);
        if (toIndex.length === 0) {
          console.log('[useAiIndexWorker] 🏷️ All files up to date');
          const enrichedTree = buildFileTreeListing(fileTree, 0, existingIndex);
          setLocalFileTreeListing(enrichedTree);
          // 🌐 Sync existing index to cloud (fire-and-forget)
          if (Object.keys(existingIndex.files).length > 0) {
            ResourceApiService.syncLocalAiIndex(existingIndex)
              .then(r => r.success && console.log(`[useAiIndexWorker] ☁️ Local index synced: ${r.synced} files`))
              .catch(() => {});
          }
          return;
        }

        console.log(`[useAiIndexWorker] 🏷️ Batch indexing: ${toIndex.length} files`);
        setIsIndexing(true);
        setIndexingProgress({ current: 0, total: toIndex.length });

        // 4. 构建 lastModified 映射
        const lastModifiedMap = getFileLastModifiedMap(fileTree);

        // 5. 批量索引
        const batchResults = await generateTagsBatch(
          localDirHandle,
          toIndex,
          lastModifiedMap,
          (current, total) => {
            if (!cancelled) setIndexingProgress({ current, total });
          },
        );

        if (cancelled) return;

        // 6. 合并结果并保存
        const updatedIndex = { ...existingIndex };
        for (const [path, tags] of Object.entries(batchResults)) {
          updatedIndex.files[path] = tags;
        }
        await writeAiIndex(localDirHandle, updatedIndex);
        setAiIndex({ ...updatedIndex });

        // 7. 重新构建带标签的文件树
        const finalTree = await scanDirectory(localDirHandle);
        const enrichedListing = buildFileTreeListing(finalTree, 0, updatedIndex);
        setLocalFileTreeListing(enrichedListing);
        console.log(`[useAiIndexWorker] 🏷️ Batch indexing complete: ${Object.keys(batchResults).length} files tagged`);

        // 🌐 Sync updated index to cloud (fire-and-forget)
        ResourceApiService.syncLocalAiIndex(updatedIndex)
          .then(r => r.success && console.log(`[useAiIndexWorker] ☁️ Local index synced: ${r.synced} files`))
          .catch(() => {});
      } catch (err) {
        console.error('[useAiIndexWorker] Background indexing error:', err);
      } finally {
        if (!cancelled) {
          setIsIndexing(false);
          setIndexingProgress(null);
        }
      }
    };

    // 延迟 1 秒开始，避免和挂载动画冲突
    const timer = setTimeout(runBackgroundIndexing, 1000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [localDirHandle]);

  // ─── 手动刷新文件树 ─────────────────────
  const refreshFileTree = useCallback(async () => {
    if (!localDirHandle) return;
    const newTree = await scanDirectory(localDirHandle);
    setLocalFileTreeListing(buildFileTreeListing(newTree, 0, aiIndex));
  }, [localDirHandle, aiIndex]);

  return {
    aiIndex,
    setAiIndex,
    isIndexing,
    indexingProgress,
    localFileTreeListing,
    setLocalFileTreeListing,
    refreshFileTree,
  };
}
